// Separate analytics service for better performance
let analytics = null;
let analyticsReady = false;

/**
 * Initialize analytics asynchronously without blocking the main thread
 */
export const initializeAnalytics = async () => {
  // Only initialize in browser environment and in production
  if (typeof window === 'undefined') {
    console.log('Analytics skipped: not in browser environment');
    return { success: false, reason: 'not_browser' };
  }

  // Don't block in development
  if (process.env.NODE_ENV !== 'production') {
    console.log('Analytics skipped: development environment');
    return { success: false, reason: 'development' };
  }

  try {
    // Dynamic import to avoid blocking the main bundle
    const { getAnalytics, isSupported } = await import('firebase/analytics');
    
    // Check if analytics is supported without blocking
    const supported = await isSupported();
    
    if (supported) {
      // Import the Firebase app after we know analytics is supported
      const { app } = await import('./firebase');
      analytics = getAnalytics(app);
      analyticsReady = true;
      console.log('Firebase Analytics initialized successfully');
      return { success: true, reason: 'initialized' };
    } else {
      console.log('Firebase Analytics not supported in this environment');
      return { success: false, reason: 'not_supported' };
    }
  } catch (error) {
    console.warn('Failed to initialize Firebase Analytics:', error);
    return { success: false, reason: 'error', error: error.message };
  }
};

/**
 * Log an event to analytics if available
 * @param {string} eventName - Name of the event
 * @param {object} eventParams - Event parameters
 */
export const logEvent = async (eventName, eventParams = {}) => {
  // In development, just log to console
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[DEV] Analytics event: ${eventName}`, eventParams);
    return;
  }

  if (!analyticsReady || !analytics) {
    console.log(`Analytics not ready, skipping event: ${eventName}`);
    return;
  }

  try {
    const { logEvent: firebaseLogEvent } = await import('firebase/analytics');
    firebaseLogEvent(analytics, eventName, eventParams);
    console.log(`Analytics event logged: ${eventName}`, eventParams);
  } catch (error) {
    console.warn('Failed to log analytics event:', error);
  }
};

/**
 * Set user properties
 * @param {object} properties - User properties to set
 */
export const setUserProperties = async (properties) => {
  // In development, just log to console
  if (process.env.NODE_ENV !== 'production') {
    console.log(`[DEV] User properties:`, properties);
    return;
  }

  if (!analyticsReady || !analytics) {
    console.log('Analytics not ready, skipping user properties');
    return;
  }

  try {
    const { setUserProperties: firebaseSetUserProperties } = await import('firebase/analytics');
    firebaseSetUserProperties(analytics, properties);
    console.log('User properties set:', properties);
  } catch (error) {
    console.warn('Failed to set user properties:', error);
  }
};

/**
 * Track page views
 * @param {string} pageName - Name of the page
 * @param {string} pageTitle - Title of the page
 */
export const trackPageView = async (pageName, pageTitle) => {
  await logEvent('page_view', {
    page_name: pageName,
    page_title: pageTitle
  });
};

export { analytics, analyticsReady }; 